import React, { useState, useMemo } from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";

const STATUS_CONFIG = {
  Paid: { color: "bg-emerald-100 text-emerald-700", icon: "CheckCircle2", dot: "bg-emerald-500" },
  Pending: { color: "bg-amber-100 text-amber-700", icon: "Clock", dot: "bg-amber-500" },
  Partial: { color: "bg-blue-100 text-blue-700", icon: "CircleDot", dot: "bg-blue-500" },
  Overdue: { color: "bg-red-100 text-red-700", icon: "AlertCircle", dot: "bg-red-500" },
  Refunded: { color: "bg-gray-100 text-gray-600", icon: "RotateCcw", dot: "bg-gray-400" }
};

const transactions = [
{ id: "INV-2026-0891", member: "Sarah Mitchell", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9d49b57-1772096831157.png", avatarAlt: "Professional woman with auburn hair in business casual attire smiling at camera", plan: "Premium Annual", amount: 1200, paid: 1200, status: "Paid", method: "Credit Card", date: "03/01/2026", aging: 0 },
{ id: "INV-2026-0892", member: "James Okafor", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14493e190-1772542096845.png", avatarAlt: "Nigerian man in his thirties with short natural hair wearing white dress shirt", plan: "Monthly Basic", amount: 89, paid: 0, status: "Overdue", method: "Cash", date: "01/28/2026", aging: 35 },
{ id: "INV-2026-0893", member: "Linda Zhao", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1961cefbc-1763299802432.png", avatarAlt: "Asian woman with straight black hair wearing teal blouse in office environment", plan: "Quarterly Plus", amount: 320, paid: 160, status: "Partial", method: "Bank Transfer", date: "02/15/2026", aging: 17 },
{ id: "INV-2026-0894", member: "Derek Patel", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_155180030-1772340617289.png", avatarAlt: "South Asian man with dark beard wearing navy polo shirt outdoors", plan: "Premium Annual", amount: 1200, paid: 0, status: "Pending", method: "UPI", date: "03/02/2026", aging: 2 },
{ id: "INV-2026-0895", member: "Amelia Torres", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_12bd700fc-1772475538331.png", avatarAlt: "Latina woman with curly brown hair wearing gym attire in fitness studio", plan: "Monthly Basic", amount: 89, paid: 89, status: "Paid", method: "Credit Card", date: "03/01/2026", aging: 0 },
{ id: "INV-2026-0896", member: "Marcus Johnson", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1ee373157-1772522029108.png", avatarAlt: "Middle-aged African American man with short hair wearing casual gray t-shirt", plan: "Personal Training", amount: 890, paid: 0, status: "Overdue", method: "Cash", date: "11/30/2025", aging: 94 },
{ id: "INV-2026-0897", member: "Priya Sharma", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_19205d2aa-1763296356182.png", avatarAlt: "Young Indian woman with long dark hair smiling in professional setting", plan: "Quarterly Plus", amount: 320, paid: 0, status: "Overdue", method: "Bank Transfer", date: "12/15/2025", aging: 78 },
{ id: "INV-2026-0898", member: "Chris Evans", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_15027bed5-1772609108884.png", avatarAlt: "Caucasian man in his twenties with light brown hair wearing athletic jacket", plan: "Monthly Basic", amount: 89, paid: 89, status: "Paid", method: "UPI", date: "03/03/2026", aging: 0 },
{ id: "INV-2026-0899", member: "Fatima Al-Hassan", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_14e5fac11-1763301397423.png", avatarAlt: "Middle Eastern woman with hijab wearing professional blazer in office setting", plan: "Premium Annual", amount: 1200, paid: 600, status: "Partial", method: "Credit Card", date: "02/01/2026", aging: 31 },
{ id: "INV-2026-0900", member: "Ryan O\'Brien", avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1c4ecb250-1772222153326.png", avatarAlt: "Irish man with red hair and freckles wearing green polo shirt outdoors", plan: "Monthly Basic", amount: 89, paid: 89, status: "Refunded", method: "Credit Card", date: "02/20/2026", aging: 12 }];


const STATUSES = ["All", "Paid", "Pending", "Partial", "Overdue", "Refunded"];

export default function PaymentTrackingTable() {
  const [statusFilter, setStatusFilter] = useState("All");
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortField, setSortField] = useState("aging");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 6;

  const filtered = useMemo(() => {
    let data = statusFilter === "All" ? transactions : transactions?.filter((t) => t?.status === statusFilter);
    data = [...data]?.sort((a, b) => {
      const aVal = a?.[sortField];
      const bVal = b?.[sortField];
      if (typeof aVal === "string") return sortDir === "asc" ? aVal?.localeCompare(bVal) : bVal?.localeCompare(aVal);
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
    return data;
  }, [statusFilter, sortField, sortDir]);

  const paginated = filtered?.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered?.length / PAGE_SIZE);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");else
    {setSortField(field);setSortDir("desc");}
  };

  const toggleRow = (id) => setSelectedRows((prev) => prev?.includes(id) ? prev?.filter((r) => r !== id) : [...prev, id]);
  const toggleAll = () => setSelectedRows(selectedRows?.length === paginated?.length ? [] : paginated?.map((r) => r?.id));

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-xl shadow-sm overflow-hidden">
      <div className="p-4 lg:p-5 border-b border-[var(--color-border)]">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-base lg:text-lg font-bold font-[var(--font-heading)] text-[var(--color-foreground)]">Payment Transactions</h3>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{filtered?.length} records · March 2026</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {selectedRows?.length > 0 &&
            <Button variant="warning" size="sm" iconName="Send" iconPosition="left">
                Remind ({selectedRows?.length})
              </Button>
            }
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left">Export</Button>
          </div>
        </div>
        <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
          {STATUSES?.map((s) =>
          <button
            key={s}
            onClick={() => {setStatusFilter(s);setPage(0);}}
            className={`px-3 py-1.5 rounded-full text-xs font-medium font-[var(--font-caption)] whitespace-nowrap transition-all duration-200 flex-shrink-0 ${statusFilter === s ? "bg-[var(--color-primary)] text-white" : "bg-[var(--color-muted)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"}`}>
            
              {s}
            </button>
          )}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead>
            <tr className="bg-[var(--color-muted)]">
              <th className="w-10 px-4 py-3">
                <input type="checkbox" checked={selectedRows?.length === paginated?.length && paginated?.length > 0} onChange={toggleAll} className="w-4 h-4 rounded accent-[var(--color-primary)]" />
              </th>
              {[
              { label: "Invoice", field: "id" },
              { label: "Member", field: "member" },
              { label: "Plan", field: "plan" },
              { label: "Amount", field: "amount" },
              { label: "Status", field: "status" },
              { label: "Method", field: "method" },
              { label: "Aging", field: "aging" }]?.
              map((col) =>
              <th key={col?.field} className="px-3 py-3 text-left">
                  <button onClick={() => toggleSort(col?.field)} className="flex items-center gap-1 text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] transition-colors">
                    {col?.label}
                    <Icon name={sortField === col?.field ? sortDir === "asc" ? "ChevronUp" : "ChevronDown" : "ChevronsUpDown"} size={12} color="currentColor" />
                  </button>
                </th>
              )}
              <th className="px-3 py-3 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)]">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-border)]">
            {paginated?.map((tx) => {
              const cfg = STATUS_CONFIG?.[tx?.status];
              const pctPaid = tx?.amount > 0 ? Math.round(tx?.paid / tx?.amount * 100) : 0;
              return (
                <tr key={tx?.id} className={`hover:bg-[var(--color-muted)]/50 transition-colors ${selectedRows?.includes(tx?.id) ? "bg-[var(--color-primary)]/5" : ""}`}>
                  <td className="px-4 py-3">
                    <input type="checkbox" checked={selectedRows?.includes(tx?.id)} onChange={() => toggleRow(tx?.id)} className="w-4 h-4 rounded accent-[var(--color-primary)]" />
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs font-[var(--font-data)] text-[var(--color-muted-foreground)]">{tx?.id}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <img src={tx?.avatar} alt={tx?.avatarAlt} className="w-7 h-7 rounded-full object-cover flex-shrink-0" onError={(e) => {e.target.src = "/assets/images/no_image.png";}} />
                      <span className="text-sm font-medium font-[var(--font-caption)] text-[var(--color-foreground)] whitespace-nowrap">{tx?.member}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">{tx?.plan}</span>
                  </td>
                  <td className="px-3 py-3">
                    <div>
                      <span className="text-sm font-bold font-[var(--font-data)] text-[var(--color-foreground)]">${tx?.amount?.toLocaleString()}</span>
                      {tx?.status === "Partial" &&
                      <div className="flex items-center gap-1 mt-1">
                          <div className="w-16 bg-[var(--color-muted)] rounded-full h-1">
                            <div className="h-1 rounded-full bg-blue-500" style={{ width: `${pctPaid}%` }} />
                          </div>
                          <span className="text-xs text-blue-600 font-[var(--font-caption)]">{pctPaid}%</span>
                        </div>
                      }
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium font-[var(--font-caption)] ${cfg?.color}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${cfg?.dot}`} />
                      {tx?.status}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{tx?.method}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs font-bold font-[var(--font-data)] ${tx?.aging === 0 ? "text-emerald-600" : tx?.aging <= 30 ? "text-amber-600" : tx?.aging <= 60 ? "text-orange-600" : "text-red-600"}`}>
                      {tx?.aging === 0 ? "Current" : `${tx?.aging}d`}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    {(tx?.status === "Pending" || tx?.status === "Overdue" || tx?.status === "Partial") &&
                    <button className="w-7 h-7 flex items-center justify-center rounded-lg bg-[var(--color-primary)]/10 hover:bg-[var(--color-primary)] group transition-colors" aria-label={`Send reminder to ${tx?.member}`}>
                        <Icon name="Send" size={13} color="var(--color-primary)" className="group-hover:text-white" />
                      </button>
                    }
                  </td>
                </tr>);

            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between px-4 py-3 border-t border-[var(--color-border)]">
        <span className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
          Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered?.length)} of {filtered?.length}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-[var(--color-border)] disabled:opacity-40 hover:bg-[var(--color-muted)] transition-colors"
            aria-label="Previous page">
            
            <Icon name="ChevronLeft" size={14} color="var(--color-foreground)" />
          </button>
          {Array.from({ length: totalPages }, (_, i) =>
          <button
            key={i}
            onClick={() => setPage(i)}
            className={`w-8 h-8 flex items-center justify-center rounded-lg text-xs font-medium font-[var(--font-caption)] transition-colors ${page === i ? "bg-[var(--color-primary)] text-white" : "border border-[var(--color-border)] hover:bg-[var(--color-muted)] text-[var(--color-foreground)]"}`}>
            
              {i + 1}
            </button>
          )}
          <button
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-[var(--color-border)] disabled:opacity-40 hover:bg-[var(--color-muted)] transition-colors"
            aria-label="Next page">
            
            <Icon name="ChevronRight" size={14} color="var(--color-foreground)" />
          </button>
        </div>
      </div>
    </div>);

}